import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, X } from 'lucide-react';
import { getEvidenceUrl, viewableImageUrl } from '../lib/fileUrls';

// Abre la galería desde cualquier tarjeta de evidencia de la página.
export const EvidenceGalleryContext = createContext(null);

// Misma regla que ya usaba la revisión para decidir si una evidencia se dibuja como foto.
export function isImageEvidence(evidence = {}) {
  return evidence.media_type === 'image' || evidence.file_type?.startsWith('image/')
    || /\.(jpe?g|png|gif|webp|heic|heif|avif)([?#]|$)/i.test(evidence.file_url || '');
}

// Clic normal → galería. Ctrl/Cmd/Shift/clic central conservan el comportamiento del enlace (otra pestaña).
export function shouldOpenGallery(event) {
  return !(event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

/**
 * Galería emergente de evidencias.
 * photos: [{ url, title, caption }] en el orden de la página; index: foto abierta (null = cerrada).
 * Teclado: ← → cambian de foto, Inicio/Fin van a la primera/última, Esc cierra.
 */
export default function EvidenceLightbox({ photos, index, onIndexChange, onClose }) {
  const open = index !== null && index !== undefined && photos.length > 0;
  const current = open ? photos[Math.min(Math.max(index, 0), photos.length - 1)] : null;
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const dialog = useRef(null);
  const strip = useRef(null);
  const touch = useRef(null);

  const go = useCallback((next) => {
    if (!photos.length) return;
    onIndexChange((next + photos.length) % photos.length);
  }, [photos.length, onIndexChange]);

  useEffect(() => { setFailed(false); setLoaded(false); }, [current?.url]);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); go(index + 1); }
      else if (event.key === 'ArrowLeft') { event.preventDefault(); go(index - 1); }
      else if (event.key === 'Home') { event.preventDefault(); onIndexChange(0); }
      else if (event.key === 'End') { event.preventDefault(); onIndexChange(photos.length - 1); }
      else if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, index, go, onClose, onIndexChange, photos.length]);

  // Precarga la anterior y la siguiente para que el cambio sea inmediato.
  useEffect(() => {
    if (!open || photos.length < 2 || typeof Image === 'undefined') return;
    [index - 1, index + 1].forEach((i) => {
      const photo = photos[(i + photos.length) % photos.length];
      const image = new Image();
      image.src = viewableImageUrl(photo.url, 1600);
    });
  }, [open, index, photos]);

  useEffect(() => {
    if (!open) return;
    const thumb = strip.current?.querySelector(`[data-thumb="${index}"]`);
    if (thumb && typeof thumb.scrollIntoView === 'function') thumb.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [open, index]);

  if (!open) return null;

  const onTouchStart = (event) => { touch.current = event.touches[0]?.clientX ?? null; };
  const onTouchEnd = (event) => {
    if (touch.current === null) return;
    const delta = (event.changedTouches[0]?.clientX ?? touch.current) - touch.current;
    touch.current = null;
    if (Math.abs(delta) > 50) go(index + (delta < 0 ? 1 : -1));
  };

  return (
    <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Galería de evidencias"
      data-testid="evidence-lightbox"
      className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 text-white outline-none"
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="flex items-center gap-3 bg-black/60 px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{current.title}</p>
          <p className="truncate text-xs text-slate-300">{current.caption}</p>
        </div>
        <span className="whitespace-nowrap text-sm text-slate-300" aria-live="polite">
          {index + 1} de {photos.length}
        </span>
        <a href={getEvidenceUrl(current.url)} target="_blank" rel="noreferrer" title="Abrir en otra pestaña"
          className="rounded p-2 text-slate-300 hover:bg-white/10 hover:text-white">
          <ExternalLink size={18} />
        </a>
        <button type="button" onClick={onClose} aria-label="Cerrar galería (Esc)"
          className="rounded p-2 text-slate-300 hover:bg-white/10 hover:text-white">
          <X size={22} />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-14"
        onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
        {photos.length > 1 && (
          <button type="button" onClick={() => go(index - 1)} aria-label="Foto anterior (←)"
            className="absolute left-2 rounded-full bg-white/10 p-2 hover:bg-white/25">
            <ChevronLeft size={30} />
          </button>
        )}
        {failed ? (
          <div className="text-center text-sm text-slate-300">
            <p>No se pudo mostrar esta foto.</p>
            <a href={getEvidenceUrl(current.url)} target="_blank" rel="noreferrer" className="underline">Abrir original</a>
          </div>
        ) : (
          <>
            {!loaded && <div className="absolute h-10 w-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />}
            <img key={current.url} src={viewableImageUrl(current.url, 1600)} alt={current.title}
              onLoad={() => setLoaded(true)} onError={() => setFailed(true)}
              className={`max-h-full max-w-full select-none object-contain transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`} />
          </>
        )}
        {photos.length > 1 && (
          <button type="button" onClick={() => go(index + 1)} aria-label="Foto siguiente (→)"
            className="absolute right-2 rounded-full bg-white/10 p-2 hover:bg-white/25">
            <ChevronRight size={30} />
          </button>
        )}
      </div>

      {photos.length > 1 && (
        <div ref={strip} className="flex gap-2 overflow-x-auto px-4 py-3">
          {photos.map((photo, i) => (
            <button key={photo.url} type="button" data-thumb={i} onClick={() => onIndexChange(i)}
              aria-label={`Ir a la foto ${i + 1}`} aria-current={i === index ? 'true' : undefined}
              className={`h-14 w-20 flex-none overflow-hidden rounded border-2 ${i === index ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}>
              <img src={viewableImageUrl(photo.url, 160)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <p className="pb-2 text-center text-[11px] text-slate-400">← → para cambiar de foto · Esc para cerrar</p>
    </div>
  );
}
